import React, { useState, useEffect, useRef } from 'react';
import PhotoEditorModal from './PhotoEditorModal';
import { toJpegOrPng } from '../utils/imageUpload';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const statuses = [
  { value: 'new', label: 'New', color: 'blue' },
  { value: 'in-progress', label: 'In Progress', color: 'yellow' },
  { value: 'in-review', label: 'In Review', color: 'purple' },
  { value: 'completed', label: 'Completed', color: 'green' }
];

const priorities = [
  { value: 'low', label: 'Low', color: 'gray' },
  { value: 'medium', label: 'Medium', color: 'blue' },
  { value: 'high', label: 'High', color: 'orange' }
];

export default function TodoModal({
  showModal,
  onClose,
  onSuccess,
  editingTodo = null
}) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'new',
    priority: 'medium',
    assignedTo: null,
    dueDate: ''
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [photos, setPhotos] = useState([]);
  const [showPhotoEditor, setShowPhotoEditor] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Load users for assignment dropdown
  useEffect(() => {
    if (showModal) {
      fetchUsers();
    }
  }, [showModal]);

  // Sync form data when editingTodo changes
  useEffect(() => {
    if (showModal && editingTodo) {
      setFormData({
        title: editingTodo.title || '',
        description: editingTodo.description || '',
        status: editingTodo.status || 'new',
        priority: editingTodo.priority || 'medium',
        assignedTo: editingTodo.assigned_to || null,
        dueDate: editingTodo.due_date ? editingTodo.due_date.split('T')[0] : ''
      });
      setPhotos(editingTodo.photos || []);
    } else if (showModal && !editingTodo) {
      setFormData({
        title: '',
        description: '',
        status: 'new',
        priority: 'medium',
        assignedTo: null,
        dueDate: ''
      });
      setPhotos([]);
    }
  }, [showModal, editingTodo]);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/todos/users`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
      }
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const body = {
        title: formData.title,
        description: formData.description || undefined,
        status: formData.status,
        priority: formData.priority,
        assignedTo: formData.assignedTo || undefined,
        dueDate: formData.dueDate || undefined,
        photos: photos
      };

      const url = editingTodo
        ? `${API_URL}/todos/${editingTodo.id}`
        : `${API_URL}/todos`;
      const method = editingTodo ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save todo');
      }

      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (input) => {
    // Support both event objects (from file input) and raw File objects (from PhotoEditorModal)
    const rawFile = input?.target?.files?.[0] || input;
    if (!rawFile) return null;

    setUploading(true);
    try {
      // Phones often hand over .webp; convert to JPEG so only jpg/png are uploaded
      const file = await toJpegOrPng(rawFile);
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const response = await fetch(`${API_URL}/uploads?folder=todos`, {
        method: 'POST',
        credentials: 'include',
        body: formDataUpload
      });
      if (response.ok) {
        const data = await response.json();
        // When called from file input, update photos directly
        // When called from PhotoEditorModal, return the result
        if (input?.target) {
          setPhotos([...photos, { url: data.url, filename: data.originalname }]);
        }
        return data;
      }
      return null;
    } catch (err) {
      setError('Failed to upload photo');
      return null;
    } finally {
      setUploading(false);
      if (input?.target) input.target.value = '';
    }
  };

  const handlePhotoSave = (updatedPhotos) => {
    setPhotos(updatedPhotos);
    setShowPhotoEditor(false);
  };

  const handlePhotoEditorClose = () => {
    setShowPhotoEditor(false);
  };

  const getStatusColor = (value) => {
    const s = statuses.find(s => s.value === value);
    return s ? s.color : 'gray';
  };

  const getPriorityColor = (value) => {
    const p = priorities.find(p => p.value === value);
    return p ? p.color : 'blue';
  };

  const colorClasses = {
    blue: 'bg-blue-100 text-blue-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    purple: 'bg-purple-100 text-purple-800',
    green: 'bg-green-100 text-green-800',
    gray: 'bg-gray-100 text-gray-800',
    orange: 'bg-orange-100 text-orange-800'
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-screen overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {editingTodo ? 'Edit Todo' : 'New Todo'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4" autoComplete="off">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded text-sm">
              {error}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              autoComplete="off"
              required
              value={formData.title}
              onChange={e => setFormData({ ...formData, title: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="What needs to be done?"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              autoComplete="off"
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Add details..."
            />
          </div>

          {/* Status & Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={e => setFormData({ ...formData, status: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                {statuses.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={formData.priority}
                onChange={e => setFormData({ ...formData, priority: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                {priorities.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Assigned To */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
            <select
              value={formData.assignedTo || ''}
              onChange={e => setFormData({ ...formData, assignedTo: e.target.value || null })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Unassigned</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>

          {/* Due Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
            <input
              type="date"
              autoComplete="off"
              value={formData.dueDate}
              onChange={e => setFormData({ ...formData, dueDate: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Photos */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Photos</label>
            <div className="flex items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : '+ Upload Photo'}
              </button>
              <span className="text-xs text-gray-400">{photos.length} photo(s)</span>
            </div>
            {photos.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {photos.map((photo, index) => (
                  <div key={index} className="relative w-20 h-20">
                    <img
                      src={photo.url}
                      alt={photo.filename}
                      className="w-full h-full object-cover rounded-md border border-gray-200 cursor-pointer"
                      onClick={() => setShowPhotoEditor(true)}
                    />
                    <button
                      type="button"
                      onClick={() => setPhotos(photos.filter((_, i) => i !== index))}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600"
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Preview badges */}
          <div className="flex gap-2 pt-2">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClasses[getStatusColor(formData.status)]}`}>
              {statuses.find(s => s.value === formData.status)?.label}
            </span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClasses[getPriorityColor(formData.priority)]}`}>
              {priorities.find(p => p.value === formData.priority)?.label}
            </span>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : (editingTodo ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>

      {/* Photo Editor Modal */}
      <PhotoEditorModal
        isOpen={showPhotoEditor}
        onClose={handlePhotoEditorClose}
        photos={photos}
        onSave={handlePhotoSave}
        onUpload={handlePhotoUpload}
      />
    </div>
  );
}
